import { Button, Empty, Modal, Progress, Select, Tag, Tooltip, message as antdMessage } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import type { CampaignDetail, CampaignRecipient, RecipientStatus } from "../api/campaigns";
import { ContactAvatar } from "./ContactAvatar";
import { formatDateTime } from "../utils/time";
import { useIsMobile } from "../hooks/useBreakpoint";
import { followupCampaign } from "../api/push";
import { listTemplates } from "../api/followupTemplates";
import type { FollowupTemplate } from "../api/followupTemplates";

const STATUS_LABEL: Record<RecipientStatus, string> = {
  queued: "排队中",
  sent: "已发送",
  read: "已读",
  replied: "已回复",
  followed_up: "已跟进",
  failed: "失败",
};

const STATUS_COLOR: Record<RecipientStatus, string> = {
  queued: "default",
  sent: "blue",
  read: "orange",
  replied: "green",
  followed_up: "purple",
  failed: "red",
};

function progressOf(c: CampaignDetail): { percent: number; sent: number; replied: number; total: number } {
  const total = c.recipients.length;
  const sent = c.recipients.filter((r) => r.status !== "queued" && r.status !== "failed").length;
  const replied = c.counts.replied ?? 0;
  return {
    percent: total === 0 ? 0 : Math.round((sent / total) * 100),
    sent,
    replied,
    total,
  };
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 14,
  color: "var(--ant-color-text-secondary)",
};
const td: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

export function StatusTable({
  campaign,
  onChange,
  onOpenThread,
}: {
  campaign: CampaignDetail;
  onChange?: () => void;
  onOpenThread?: (threadId: string) => void;
}) {
  const isMobile = useIsMobile();
  const openThread = (r: CampaignRecipient) => {
    if (r.threadId && onOpenThread) {
      onOpenThread(r.threadId);
    } else {
      antdMessage.info("还没有对应的邮件往来,等待 Gmail 同步…");
    }
  };
  const { percent, sent, replied, total } = progressOf(campaign);
  const eligible = campaign.recipients.filter(
    (r) => r.status === "sent" || r.status === "read",
  ).length;
  const [followingUp, setFollowingUp] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [templates, setTemplates] = useState<FollowupTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!modalOpen) return;
    setTemplatesLoading(true);
    listTemplates()
      .then((d) => {
        setTemplates(d.templates);
        if (d.templates.length > 0 && !selectedTemplateId) {
          setSelectedTemplateId(d.templates[0]!.id);
        }
      })
      .finally(() => setTemplatesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const onFollowup = async () => {
    if (!selectedTemplateId) {
      antdMessage.warning("请选择一个模板");
      return;
    }
    setFollowingUp(true);
    try {
      const out = await followupCampaign(campaign.id, selectedTemplateId);
      const skippedCount = out.skipped.length;
      const failedCount = out.failed.length;
      if (out.sent > 0) {
        antdMessage.success(
          `已跟进 ${out.sent} 人${skippedCount ? `,跳过 ${skippedCount}` : ""}${failedCount ? `,失败 ${failedCount}` : ""}`,
        );
      } else if (skippedCount + failedCount === 0) {
        antdMessage.info("没有需要跟进的人");
      } else {
        antdMessage.warning(
          `本次未发出${skippedCount ? `,跳过 ${skippedCount}` : ""}${failedCount ? `,失败 ${failedCount}` : ""}`,
        );
      }
      setModalOpen(false);
      onChange?.();
    } catch {
      antdMessage.error("跟进失败");
    } finally {
      setFollowingUp(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>主题</div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 17,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {campaign.subject}
          </div>
          <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)", marginTop: 2 }}>
            {formatDateTime(campaign.createdAt)} · {sent}/{total} 已发送 · {replied} 已回复
          </div>
        </div>
        <div style={{ minWidth: 200, display: "flex", alignItems: "center", gap: 8 }}>
          <Progress percent={percent} size="small" style={{ flex: 1 }} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type={eligible > 0 ? "primary" : "default"}
          icon={<SendOutlined />}
          disabled={eligible === 0}
          onClick={() => setModalOpen(true)}
        >
          一键跟进 {eligible > 0 ? `(${eligible})` : ""}
        </Button>
      </div>

      <Modal
        title="发送跟进邮件"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onFollowup}
        okText={`确认跟进 (${eligible})`}
        cancelText="取消"
        okButtonProps={{ disabled: !selectedTemplateId || eligible === 0, loading: followingUp }}
        width={Math.min(560, typeof window !== "undefined" ? window.innerWidth - 40 : 560)}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 15, color: "var(--ant-color-text-secondary)" }}>
            将给 <strong>{eligible}</strong> 位未回复成员发送跟进。已回复的人会自动跳过。
          </div>
          <div>
            <div
              style={{
                fontSize: 14,
                color: "var(--ant-color-text-secondary)",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              选择跟进模板
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedTemplateId}
              onChange={(v) => setSelectedTemplateId(v)}
              loading={templatesLoading}
              placeholder={
                templatesLoading
                  ? "加载中…"
                  : templates.length === 0
                  ? "Settings 里还没建模板"
                  : "选择模板"
              }
              disabled={templates.length === 0}
              options={templates.map((t) => ({ value: t.id, label: t.name }))}
            />
          </div>
          {selectedTemplate ? (
            <div
              style={{
                background: "var(--ant-color-bg-layout)",
                border: "1px solid var(--ant-color-border)",
                borderRadius: 10,
                padding: 12,
              }}
            >
              <Tag color="purple" style={{ marginBottom: 6 }}>
                预览
              </Tag>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: 15,
                  lineHeight: 1.6,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {selectedTemplate.body}
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
              先去 <a href="/settings">设置 → 群组跟进模板</a> 建一个。
            </div>
          ) : null}
        </div>
      </Modal>

      {campaign.recipients.length === 0 ? (
        <Empty description="无收件人" />
      ) : isMobile ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {campaign.recipients.map((r) => (
            <RecipientCard key={r.id} r={r} onOpen={() => openThread(r)} />
          ))}
        </ul>
      ) : (
        <div
          style={{
            background: "var(--ant-color-bg-container)",
            border: "1px solid var(--ant-color-border)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
            <thead>
              <tr style={{ background: "var(--ant-color-bg-layout)" }}>
                <th style={th}>联系人</th>
                <th style={th}>状态</th>
                <th style={th}>最后动作</th>
              </tr>
            </thead>
            <tbody>
              {campaign.recipients.map((r) => {
                const clickable = Boolean(r.threadId);
                return (
                  <tr
                    key={r.id}
                    onClick={() => openThread(r)}
                    style={{
                      borderTop: "1px solid var(--ant-color-border)",
                      cursor: clickable ? "pointer" : "default",
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => {
                      if (clickable)
                        e.currentTarget.style.background =
                          "color-mix(in srgb, var(--ant-color-primary) 6%, transparent)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                    title={clickable ? "查看邮件往来" : "等待 Gmail 同步"}
                  >
                    <td style={td}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <ContactAvatar email={r.email} name={r.name} tier="unimportant" size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{r.name || r.email}</div>
                          {r.name ? (
                            <div
                              style={{
                                fontSize: 14,
                                color: "var(--ant-color-text-secondary)",
                              }}
                            >
                              {r.email}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {r.status === "failed" && r.error ? (
                          <Tooltip title={r.error}>
                            <Tag color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Tag>
                          </Tooltip>
                        ) : (
                          <Tag color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Tag>
                        )}
                        {r.hasUnreadReply ? (
                          <Tag
                            color="volcano"
                            style={{ marginInlineEnd: 0, fontWeight: 600 }}
                          >
                            ● 新回复
                          </Tag>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ ...td, color: "var(--ant-color-text-secondary)" }}>
                      {lastActionTime(r)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function lastActionTime(r: CampaignRecipient): string {
  const ts =
    r.repliedAt ?? r.followedUpAt ?? r.readAt ?? r.sentAt;
  return ts ? formatDateTime(ts) : "—";
}

function RecipientCard({ r, onOpen }: { r: CampaignRecipient; onOpen: () => void }) {
  const clickable = Boolean(r.threadId);
  return (
    <li
      onClick={onOpen}
      style={{
        background: "var(--ant-color-bg-container)",
        border: "1px solid var(--ant-color-border)",
        borderRadius: 12,
        padding: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <ContactAvatar email={r.email} name={r.name} tier="unimportant" size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{r.name || r.email}</div>
        <div
          style={{
            fontSize: 14,
            color: "var(--ant-color-text-secondary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lastActionTime(r)}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        {r.status === "failed" && r.error ? (
          <Tooltip title={r.error}>
            <Tag color={STATUS_COLOR[r.status]} style={{ marginInlineEnd: 0 }}>
              {STATUS_LABEL[r.status]}
            </Tag>
          </Tooltip>
        ) : (
          <Tag color={STATUS_COLOR[r.status]} style={{ marginInlineEnd: 0 }}>
            {STATUS_LABEL[r.status]}
          </Tag>
        )}
        {r.hasUnreadReply ? (
          <Tag color="volcano" style={{ marginInlineEnd: 0, fontWeight: 600 }}>
            ● 新回复
          </Tag>
        ) : null}
      </div>
    </li>
  );
}
